import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormControl,
} from '@angular/forms';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NotificationService } from '../../../../Services/notification.service';
import { AssetService } from '../../../../Services/asset.service';
import { Asset } from '../../../../Models/asset';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-assets-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule,RouterLink],
  templateUrl: './assets-form.component.html',
  styleUrls: ['./assets-form.component.css'],
})
export class AssetsFormComponent implements OnInit {
  assetForm!: FormGroup;
  isSubmitting = false;

  searchControl = new FormControl('');

  private allAssets: Asset[] = [];
  latestAssets: Asset[] = [];

  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalItems: number = 0;
  totalPages: number = 0;

  assignedAssetsCount: number = 0;
  availableAssetsCount: number = 0;

  constructor(
    private fb: FormBuilder,
    private assetService: AssetService,
    private router: Router,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.initAssetForm();
    this.loadAssets();

    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.currentPage = 1;
        this.updatePagedAssets();
      });
  }

  initAssetForm(): void {
    this.assetForm = this.fb.group({
      id: [null],
      date: [this.getTodaysDate()],
      asset_name: ['', [Validators.required, Validators.maxLength(255)]],
      value: [null, [Validators.required, Validators.min(0)]],
      purchase_date: [this.getTodaysDate(), Validators.required],
    });
  }

  loadAssets(): void {
    this.assetService.getAssets().subscribe({
      next: (res: any) => {
        this.allAssets = (res.data || []).sort(
          (a: Asset, b: Asset) => b.id! - a.id!
        );
        this.updatePagedAssets();
      },
      error: (err) =>
        this.notificationService.showError(
          'Could not fetch assets.',
          'API Error'
        ),
    });
  }

  updatePagedAssets(): void {
    const searchTerm = (this.searchControl.value || '').toLowerCase();

    const filteredAssets = searchTerm
      ? this.allAssets.filter((asset) =>
          (asset.name || asset.name || '').toLowerCase().includes(searchTerm)
        )
      : [...this.allAssets];

    this.assignedAssetsCount = filteredAssets.filter(
      (asset) => asset.kalakar_id !== null
    ).length;
    this.availableAssetsCount = filteredAssets.filter(
      (asset) => asset.kalakar_id === null
    ).length;
    this.totalItems = filteredAssets.length;
    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);

    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.latestAssets = filteredAssets.slice(startIndex, endIndex);
  }

  private getTodaysDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = ('0' + (today.getMonth() + 1)).slice(-2);
    const day = ('0' + today.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  }

  onSubmit(): void {
    this.isSubmitting = true;
    const formData: Asset = this.assetForm.value;
    const assetId = this.assetForm.get('id')?.value;

    const apiCall = assetId
      ? this.assetService.updateAsset(assetId, formData)
      : this.assetService.createAsset(formData);

    apiCall.subscribe({
      next: () => {
        this.notificationService.showSuccess(
          assetId ? 'Asset updated!' : 'Asset created!',
          'Success'
        );
        this.resetForm();
        this.loadAssets();
      },
      error: (err) => {
        this.isSubmitting = false;
      },
      complete: () => {
        this.isSubmitting = false;
      },
    });
  }

  patchFormForEdit(asset: Asset): void {
    this.assetForm.patchValue({
      id: asset.id,
      date: asset.date,
      asset_name: asset.name || asset.name,
      value: asset.value,
      purchase_date: asset.purchase_date,
    });
    window.scrollTo(0, 0);
  }

  deleteAsset(assetId: number | undefined): void {
    if (!assetId) return;
    if (confirm('Are you sure you want to delete this asset?')) {
      this.assetService.deleteAsset(assetId).subscribe({
        next: () => {
          this.notificationService.showSuccess(
            'Asset deleted successfully!',
            'Success'
          );
          this.loadAssets();
        },
        error: (err) =>
          this.notificationService.showError(
            'Failed to delete asset.',
            'API Error'
          ),
      });
    }
  }

  onReturnAsset(assetId: number | undefined): void {
    if (!assetId) return;

    if (
      confirm('Are you sure you want to return this asset to the inventory?')
    ) {
      const payload = {
        kalakar_id: null,
        date: null,
        receipt_acknowledged_at: null,
        issue_reported_at: null,
      };

      this.assetService.updateAsset(assetId, payload).subscribe({
        next: () => {
          this.notificationService.showSuccess(
            'Asset returned successfully!',
            'Success'
          );
          this.loadAssets();
        },
        error: (err) => {
          this.notificationService.showError(
            'Failed to return asset.',
            'API Error'
          );
          console.error(err);
        },
      });
    }
  }

  resetForm(): void {
    this.assetForm.reset({
      id: null,
      date: this.getTodaysDate(),
      purchase_date: this.getTodaysDate(),
      asset_name: '',
      value: null,
    });
    this.searchControl.setValue('');
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagedAssets();
    }
  }

  onNextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  onPrevPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  focusNext(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key === 'Enter') {
      keyboardEvent.preventDefault();
      const target = keyboardEvent.target as HTMLElement;
      const form = target.closest('form');
      if (!form) return;
      const focusable = Array.from(
        form.querySelectorAll<HTMLElement>('input, select, textarea, button')
      ).filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1);
      const index = focusable.indexOf(target);
      if (index > -1 && index + 1 < focusable.length) {
        focusable[index + 1].focus();
      }
    }
  }
}
